import * as Ariakit from "@ariakit/react"
import { type WebArticle } from "@prisma/client"
import { json, type LoaderFunctionArgs } from "@remix-run/node"
import { useLoaderData, type ShouldRevalidateFunctionArgs, useFetcher } from "@remix-run/react"
import { useState, useEffect, useId } from "react"
import { ArticleCard } from "#app/components/article-card"
import { Icon } from "#app/components/ui/icon"
import { getPopularContent } from "#app/models/articles"
import { type action } from "#app/routes/_library+/add"
import { getUserId } from "#app/utils/auth.server"
import { cn, getAppPageTitle } from "#app/utils/misc"

type PromisesObject<T> = {
	[K in keyof T]: Promise<T[K]>
}

function promiseAllObject<T>(obj: PromisesObject<T>): Promise<T> {
	const keys = Object.keys(obj) as Array<keyof T>
	const values = keys.map(key => obj[key])

	return Promise.all(values).then(resolvedValues => {
		const resolvedObject = {} as T
		resolvedValues.forEach((value, index) => {
			resolvedObject[keys[index]] = value
		})
		return resolvedObject
	})
}

export async function loader({ request }: LoaderFunctionArgs) {
	const limit = process.env.NODE_ENV === "development" ? 10 : 25
	const userId = await getUserId(request)
	const articles = await promiseAllObject({
		quickReads: getPopularContent({ userId, limit, filters: { readingTime: { lte: 5 } } }),
		longReads: getPopularContent({ userId, limit, filters: { readingTime: { gte: 20 } } }),
		popular: getPopularContent({ userId, limit }),
	})
	return json({ title: "Discover", ...articles })
}

export function meta() {
	return [{ title: getAppPageTitle("Discover") }]
}

export function shouldRevalidate({
	defaultShouldRevalidate,
	formAction,
}: ShouldRevalidateFunctionArgs) {
	if (formAction === "/add") {
		return false
	}
	return defaultShouldRevalidate
}

function SaveArticleButton({
	url,
	variant = "responsive",
	onSave,
}: {
	url: string
	variant?: "responsive" | "icon-only"
	onSave?: (newUrl: string) => void
}) {
	const fetcher = useFetcher<typeof action>()
	const articleId =
		fetcher.state === "idle" && fetcher.data?.status === "success"
			? fetcher.data.articleId
			: null
	const isSaved = articleId !== null
	const labelClassName =
		variant === "responsive"
			? "sr-only sm:not-sr-only"
			: variant === "icon-only"
				? "sr-only"
				: undefined

	const className = cn("btn btn-xs btn-ghost flex h-6 items-center justify-center", {
		"w-6 sm:w-auto": variant === "responsive",
		"w-6": variant === "icon-only",
		"hover:!bg-transparent": isSaved,
	})

	useEffect(() => {
		if (articleId != null) {
			onSave?.(`/article/${articleId}`)
		}
	}, [articleId, onSave])

	if (isSaved) {
		return (
			<span className={className}>
				<Icon name="check" size="sm">
					<span className={labelClassName}>Saved</span>
				</Icon>
			</span>
		)
	}

	return (
		<button
			type="button"
			tabIndex={-1}
			className={className}
			onClick={() => {
				const formData = new FormData()
				formData.append("url", url)
				fetcher.submit(formData, { method: "post", action: "/add" })
			}}
		>
			<Icon size="sm" name={fetcher.state === "idle" ? "download" : "loading"}>
				<span className={labelClassName}>
					{fetcher.state !== "idle" ? "Saving…" : "Save"}
				</span>
			</Icon>
		</button>
	)
}

interface DiscoverableArticle
	extends Pick<WebArticle, "id" | "title" | "url" | "imageUrl" | "readingTime" | "siteName"> {
	className?: string
}

function DiscoverArticleCard({ className, id, url, ...article }: DiscoverableArticle) {
	const [linkTo, setLinkTo] = useState(url)
	return (
		<ArticleCard {...article} linkTo={linkTo} className={className}>
			<SaveArticleButton url={url} onSave={setLinkTo} />
		</ArticleCard>
	)
}

export default function DiscoverPage() {
	const { popular, quickReads, longReads } = useLoaderData<typeof loader>()

	return (
		<div className="mx-auto flex flex-grow flex-col gap-2 overflow-y-auto py-4">
			<div className="flex items-center justify-between">
				<h1 className="sr-only">Discover</h1>
			</div>
			<ArticlesCategory title="Popular" articles={popular} />
			<ArticlesCategory title="Quick reads" articles={quickReads} />
			<ArticlesCategory title="Long reads" articles={longReads} />
		</div>
	)
}

function ArticlesCategory({ title, articles }: { title: string; articles: DiscoverableArticle[] }) {
	const composite = Ariakit.useCompositeStore({ orientation: "horizontal" })
	const id = useId()
	const titleElementId = `category-title-${id}`
	return (
		<div className="fadeout-scroller-container space-y-1 py-4">
			<div className="px-[var(--side-padding)]">
				<div className="mx-auto flex max-w-7xl items-center">
					<h2 id={titleElementId} className="text-h2">
						{title}
					</h2>
				</div>
			</div>
			<Ariakit.Composite
				store={composite}
				render={<ul />}
				aria-labelledby={titleElementId}
				className={cn(
					"fadeout-scroller flex gap-8 overflow-x-scroll overscroll-x-contain py-4",
					"snap-x snap-mandatory px-[var(--scroll-padding)] max-sm:flex-col",
				)}
			>
				{articles.map(article => (
					<DiscoverArticleCard
						key={article.id}
						className="fadeout-scroller-card sm:last:mr-[var(--end-margin)]"
						{...article}
					/>
				))}
			</Ariakit.Composite>
		</div>
	)
}
